import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiPlus, FiServer } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../styles/theme';
import { parametrosWhatsappAPI } from '../services/api';

import ParametrosWhatsappCard from '../components/parametrosWhatsapp/ParametrosWhatsappCard';
import ParametrosWhatsappFiltros from '../components/parametrosWhatsapp/ParametrosWhatsappFiltros';
import ParametrosWhatsappPaginacao from '../components/parametrosWhatsapp/ParametrosWhatsappPaginacao';
import Layout from '../components/Layout';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[8]};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing[4]};
  }
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const TitleIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.xl};
  background: ${theme.colors.primary[500]}20;
  color: ${theme.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
`;

const TitleInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  margin: ${theme.spacing[1]} 0 0 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${theme.colors.primary[500]}50;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]};
  color: ${theme.colors.text.secondary};
  gap: ${theme.spacing[4]};

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid ${theme.colors.border.primary};
    border-top-color: ${theme.colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]};
  background: ${theme.colors.background.secondary};
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  color: ${theme.colors.text.secondary};

  h3 {
    color: ${theme.colors.text.primary};
    font-size: ${theme.fontSizes.lg};
    margin: 0 0 ${theme.spacing[2]} 0;
  }

  p {
    margin: 0;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  color: ${theme.colors.error[500]};
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing[6]};
  border: 1px solid ${theme.colors.error[500]}40;
`;

const ParametrosWhatsapp = () => {
  const navigate = useNavigate();
  const [parametros, setParametros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado para filtros e paginação
  const [filtros, setFiltros] = useState({
    search: '',
    page: 1,
    limit: 10
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const carregarDados = useCallback(async (filtrosAtuais) => {
    try {
      setLoading(true);
      setError(null);

      const response = await parametrosWhatsappAPI.listar(filtrosAtuais);
      
      if (response.data.success) {
        setParametros(response.data.data);
        setPagination({
          ...response.data.pagination,
          itemsPerPage: filtrosAtuais.limit
        });
      } else {
        setError(response.data.error || 'Erro ao carregar parâmetros do WhatsApp');
      }
    } catch (err) {
      console.error('Erro ao carregar parâmetros do WhatsApp:', err);
      setError('Erro ao carregar os parâmetros. Verifique sua conexão.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados(filtros);
  }, [filtros.page, filtros.limit]); // Removido carregarDados das dependências para evitar loop

  const handleBuscar = (novosFiltros = filtros) => {
    setFiltros({ ...novosFiltros, page: 1 });
    carregarDados({ ...novosFiltros, page: 1 });
  };

  const handleFiltrosChange = (novosFiltros) => {
    setFiltros(novosFiltros);
  };

  const handlePageChange = (newPage) => {
    setFiltros(prev => ({ ...prev, page: newPage }));
  };

  const handleItemsPerPageChange = (newLimit) => {
    setFiltros(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  return (
    <Layout title="Parâmetros WhatsApp">
      <PageContainer>
        <Header>
          <TitleBlock>
            <TitleIcon>
              <FiServer />
            </TitleIcon>
            <TitleInfo>
              <Title>Parâmetros WhatsApp</Title>
              <Subtitle>Gerencie as configurações e credenciais da API Evolution</Subtitle>
            </TitleInfo>
          </TitleBlock>
          <AddButton onClick={() => navigate('/parametros-whatsapp/novo')}>
            <FiPlus />
            Novo Parâmetro
          </AddButton>
        </Header>

        <ParametrosWhatsappFiltros
          filtros={filtros}
          onFiltrosChange={handleFiltrosChange}
          onBuscar={() => handleBuscar()}
          loading={loading}
        />

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading ? (
          <LoadingState>
            <div className="spinner" />
            <span>Carregando parâmetros...</span>
          </LoadingState>
        ) : parametros.length === 0 ? (
          <EmptyState>
            <h3>Nenhum parâmetro encontrado</h3>
            <p>Não há configurações de WhatsApp cadastradas ou nenhuma corresponde aos filtros atuais.</p>
          </EmptyState>
        ) : (
          <>
            <Grid>
              {parametros.map((parametro) => (
                <ParametrosWhatsappCard 
                  key={parametro.id} 
                  parametro={parametro} 
                />
              ))}
            </Grid>

            <ParametrosWhatsappPaginacao
              pagination={pagination}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              loading={loading}
            />
          </>
        )}
      </PageContainer>
    </Layout>
  );
};

export default ParametrosWhatsapp;
